import React, { useState, useEffect } from 'react';
import '../styles/SourceSelector.css';

const SourceSelector = ({ currentSource, onSourceChange, userRole }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    loadAvailableSources();
  }, [userRole]);

  const loadAvailableSources = async () => {
    try {
      setLoading(true);
      
      // 1. Charger les permissions utilisateur
      console.log('🔍 [SourceSelector] Chargement permissions pour userRole:', userRole);
      const permissionsResponse = await fetch('http://localhost:5001/api/dashboard/permissions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      let userPermissions = {};
      if (permissionsResponse.ok) {
        const permissionsData = await permissionsResponse.json();
        userPermissions = permissionsData.data;
        setPermissions(userPermissions);
        console.log('🔐 [SourceSelector] Permissions chargées:', userPermissions);
      }
      
      // 🔒 Si l'utilisateur est une conseillère, ne pas afficher le sélecteur
      if (userRole === 'conseillere' || userPermissions.userRole === 'conseillere') {
        console.log('🔒 [SourceSelector] Conseillère détectée - masquage complet du sélecteur');
        setSources([]);
        setLoading(false);
        return; // Arrêter ici pour les conseillères
      }
      
      const response = await fetch('http://localhost:5001/api/leads-hybrid/status', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setStatus(result.data);
        
        const availableSources = [];
        
        // Base de données toujours disponible
        availableSources.push({
          value: 'database',
          label: '🗄️ Base de données',
          description: 'Leads stockés dans la base de données locale',
          available: result.data.connections?.database?.success || true
        });
        
        // 🔒 Google Sheets et Hybride SEULEMENT si permissions accordées
        if (userPermissions.showGoogleSheetsSection && 
            userPermissions.showHybridModeSection &&
            result.data.useGoogleSheets && 
            result.data.connections?.googleSheets?.success) {
          
          console.log('✅ [SourceSelector] Permissions Google Sheets accordées');
          
          availableSources.push({
            value: 'sheets',
            label: '📋 Google Sheets',
            description: 'Leads depuis Google Spreadsheet (assignation automatique équitable)',
            available: true
          });
          
          availableSources.push({
            value: 'hybrid',
            label: '🔄 Hybride',
            description: 'Base de données + Google Sheets (assignation automatique équitable)',
            available: true
          });
        } else {
          console.log('🔒 [SourceSelector] Google Sheets masqué pour ce rôle:', userPermissions.userRole);
        }
        
        setSources(availableSources);
      }
    } catch (error) {
      console.error('Erreur chargement sources:', error);
      // Fallback avec base de données uniquement
      setSources([{
        value: 'database',
        label: '🗄️ Base de données',
        description: 'Leads stockés dans la base de données locale',
        available: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSourceChange = (newSource) => {
    if (onSourceChange) {
      onSourceChange(newSource);
    }
  };

  if (loading) {
    return (
      <div className="source-selector loading">
        <span>🔄 Chargement des sources...</span>
      </div>
    );
  }

  // 🔒 Si conseillère ou aucune source disponible, ne pas afficher le sélecteur
  const isConseillere = userRole === 'conseillere' || permissions.userRole === 'conseillere';
  if (isConseillere || sources.length === 0) {
    console.log('🔒 [SourceSelector] Masquage du sélecteur pour conseillère ou sources vides');
    return null; // Complètement masqué
  }

  return (
    <div className="source-selector">
      <div className="source-selector-header">
        <span className="source-icon">📊</span>
        <span className="source-label">Source des données :</span>
      </div>
      
      <div className="source-options">
        {sources.map(source => (
          <label 
            key={source.value}
            className={`source-option ${currentSource === source.value ? 'active' : ''} ${!source.available ? 'disabled' : ''}`}
          >
            <input
              type="radio"
              name="leadSource"
              value={source.value}
              checked={currentSource === source.value}
              onChange={() => handleSourceChange(source.value)}
              disabled={!source.available}
            />
            <div className="source-info">
              <div className="source-name">{source.label}</div>
              <div className="source-description">{source.description}</div>
              {!source.available && (
                <div className="source-status error">❌ Non disponible</div>
              )}
            </div>
          </label>
        ))}
      </div>
      
      {status && !isConseillere && (
        <div className="source-status-info">
          <div className="status-item">
            <span>🏛️ Mode actuel:</span>
            <span className="status-value">{status.syncMode}</span>
          </div>
          <div className="status-item">
            <span>📋 Google Sheets:</span>
            <span className={`status-value ${status.useGoogleSheets ? 'enabled' : 'disabled'}`}>
              {status.useGoogleSheets ? 'Activé' : 'Désactivé'}
            </span>
          </div>
          {status.useGoogleSheets && (
            <div className="status-item">
              <span>🎯 Assignation auto:</span>
              <span className="status-value enabled">
                ✅ Leads Google Sheets assignés équitablement
              </span>
            </div>
          )}
          {status.lastSyncTime && (
            <div className="status-item">
              <span>🕒 Dernière sync:</span>
              <span className="status-value">
                {new Date(status.lastSyncTime).toLocaleString('fr-FR')}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SourceSelector;
